import Backbone from "backbone";
import _ from "underscore";
import naturalSort from "javascript-natural-sort";
import {FloorPlanModel} from "../floor-plan-model";
import {Filter} from "./filter";
import {dedup, flatten} from "utils/arrays";

const FloorPlanCollection = Backbone.Collection.extend({
    model: FloorPlanModel,
    initialize: function(models, options) {
        let opts = _.defaults({}, options, {
            active: null,
            activeUnit: null
        });

        this.active = opts.active;
        this.activeUnit = opts.activeUnit;
        this.disableAvailabilitySort = opts.disableAvailabilitySort || false;
        this.alternateFloorPlanSort = opts.alternateFloorPlanSort || false;
        this.rentTextOverrideText = opts.rentTextOverrideText || "";

        this.date = null;

        this.filter = Filter();
    },
    comparatorWithoutAvailability: function(a, b) {
        return multiComparator([
            [propCompare, "bed", a, b],
            [propCompare, "order", a, b],
            [naturalSort, a.get("name"), b.get("name")]
        ]);
    },
    comparator: function(a, b) {
        return multiComparator([
            [propCompare, "bed", a, b],
            [compareAvailabilities, a, b],
            [propCompare, "order", a, b],
            [naturalSort, a.get("name"), b.get("name")]
        ]);
    },
    alternateComparator: function(a, b) {
        return multiComparator([
            [propCompare, "order", a, b],
            [propCompare, "bed", a, b],
            [naturalSort, a.get("name"), b.get("name")]
        ]);
    },
    parse: function(data) {
        let rentTextOverrideText = this.rentTextOverrideText;
        let parsed = _.map(data, function(fp) {
            fp.group_name = fp.display_bed;
            fp.display_bed = computeDisplayBed(fp);

            // Replace non-monetary values/ranges(ie: Call for details) with placeholder text
            if (fp.rent.replaceAll(/[/$\d/\s\-\.]/g, "") && rentTextOverrideText) {
                fp.rent = rentTextOverrideText;
            }

            return fp;
        });

        return parsed;
    },
    getActive: function() {
        let active = null;

        if (this.active) {
            active = this.findWhere({id: this.active});
        } else if (this.length) {
            active = this.firstByGroup();
            this.setActive(active.get("id"), false);
        }

        return active;
    },
    getHideUnitNumbers: function() {
        let hideUnitNumbers = false;

        if (this.length) {
            let fp = this.first();
            hideUnitNumbers = fp.getHideUnitNumbers();
        }
        return hideUnitNumbers;
    },
    setActive: function(id, trigger=true) {
        let prev = this.active;
        if (id) {
            if (!this.findWhere({id: id})) {
               return;
            }
            this.active = id;
        } else {
            this.active = null;
        }

        if (prev !== id) {
            this.setActiveUnit(null, false);
        }

        if (trigger) {
            this.trigger("select select:fp");
        }
    },
    groups: function() {
        let groups = new Map();

        let g = _.groupBy(this.filter.run(this), function(fp) {
            return fp.get("group_name");
        });

        let entries = Object.entries(g);

        if (this.alternateFloorPlanSort) {
            entries.sort(compareOrder);
        } else if (this.disableAvailabilitySort) {
            entries.sort(compareGroupsWithoutAvailability);
        } else {
            entries.sort(compareGroups);
        }

        for (let [k, v] of entries) {
            if (this.alternateFloorPlanSort) {
                v.sort(this.alternateComparator);
            } else if (this.disableAvailabilitySort) {
                v.sort(this.comparatorWithoutAvailability);
            } else {
                v.sort(this.comparator);
            }

            groups.set(k, v);
        }

        return groups;
    },
    firstByGroup: function() {
        let groups = this.groups();

        for (let [k, v] of groups) {
            return v[0];
        }

        return null;
    },
    getActiveUnit: function() {
        let active = null,
            units = null,
            fp = this.getActive();
        if (fp) {
            units = fp.getUnits();

            if (!units.length) {
                return null;
            }

            let activeIndex = 0;

            if (this.activeUnit) {
                for (let i = 0; i < units.length; i++) {
                    if (units[i].get("id") === this.activeUnit) {
                        activeIndex = i;
                        break;
                    }
                }
            }

            active = units[activeIndex];
            this.setActiveUnit(active.get("id"), false);
        }
        return active;
    },
    setActiveUnit: function(id, trigger=true) {
        let fp = this.getActive();
        let units = null;

        if (fp) {
            units = fp.get("units");
        }

        if (id && units &&  units.length && units.findWhere({id: id})) {
            this.activeUnit = id;
        } else {
            this.activeUnit = null;
        }

        if (trigger) {
            this.trigger("select select:unit");
        }
    },
    filterMaxRent: function(max) {
        this.filter.setMaxRentValue(max);
        this.trigger("filter");
    },
    filterUnitNumber: function(number) {
        this.filter.setUnitNumberFilterValue(number);
        this.trigger("filter");
    },
    filterDate: function(date) {
        this.filter.setDateFilterValue(date);
        this.trigger("filter");
    },
    toggleFilterCheckbox: function(listName, value) {
        let checklists = this.filter.getCheckboxFilter();
        checklists.toggle(listName, value);
        this.trigger("filter");
    },
    _buildChecklists: function() {
        let checkboxFilter = this.filter.getCheckboxFilter();

        const amenityFilterName = "Amenities";

        checkboxFilter.addList("Beds", "display_bed");
        dedup(this.pluck("display_bed")).forEach(function(bed) {
            checkboxFilter.addOption("Beds", bed);
        });

        checkboxFilter.addList("Baths", "display_bath");
        dedup(this.pluck("display_bath")).forEach(function(bath) {
            checkboxFilter.addOption("Baths", bath);
        });

        checkboxFilter.addList("Floor Plans", "getFloorPlanName");
        this.pluck("name").forEach(function(fp) {
            checkboxFilter.addOption("Floor Plans", fp);
        });

        let nestedAmenities = this.pluck("units").map(function(unitCollection) {
            return unitCollection.pluck("vendor_amenities");
        });
        let amenities = dedup(flatten(nestedAmenities));
        if (amenities.length) {
            checkboxFilter.addList("Amenities", "getVendorAmenities");
            amenities.forEach(function(amenity) {
                checkboxFilter.addOption("Amenities", amenity);
            });
        }
    }
});

function compareAvailabilities(a, b) {
    const aHasUnits = Math.min(1, a.get("units").length);
    const bHasUnits = Math.min(1, b.get("units").length);

    if (aHasUnits !== bHasUnits) {
        return bHasUnits - aHasUnits;
    }

    return b.get("availableCount") - a.get("availableCount");
}

function multiComparator(comparisons) {
    for (const row of comparisons) {
        const comparison = row.shift();
        const value = comparison.apply(this, row);
        if (value !== 0) {
            return value;
        }
    }

    return 0;
}

function compareGroupsWithoutAvailability(a, b) {
    return multiComparator([
        [compareGroupAverageBedCount, a, b],
        [naturalSort, a[0], b[0]]
    ]);
}

function compareGroups(a, b) {
    return multiComparator([
        [compareGroupHasUnits, a, b],
        [compareGroupAverageBedCount, a, b],
        [naturalSort, a[0], b[0]]
    ]);
}

function compareOrder(a, b) {
    return multiComparator([
        [compareGroupAverageOrder, a, b],
        [naturalSort, a[0], b[0]]
    ]);

}

function compareGroupHasUnits(a, b) {
    const aCount = groupHasUnits(a);
    const bCount = groupHasUnits(b);
    return bCount - aCount;
}

function groupHasUnits(group) {
    // return 1 if group has units, else return 0.
    let count = 0;
    for (const fp of group[1]) {
        count += fp.get("units").length;
    }

    return Math.min(1, count);
}

function compareGroupAverageOrder(a, b) {
    const averageOrderForA = getAverageOrder(a);
    const averageOrderForB = getAverageOrder(b);

    return averageOrderForA - averageOrderForB;
}

function getAverageOrder(group) {
    let orderCount = 0;
    for (const fp of group[1]) {
        orderCount += fp.get("order");
    }

    return orderCount / group[1].length;
}

function compareGroupAverageBedCount(a, b) {
    const averageBedsForA = getAverageNumberOfBeds(a);
    const averageBedsForB = getAverageNumberOfBeds(b);

    return averageBedsForA - averageBedsForB;
}

function getAverageNumberOfBeds(group) {
    let bedCount = 0;
    for (const fp of group[1]) {
        bedCount += fp.bed;
    }

    return bedCount / group[1].length;
}

function propCompare(prop, a, b) {
    // Compare numeric properties of two models for sorting.
    return a.get(prop) - b.get(prop);
}

function computeDisplayBed(fp) {
    if (fp.bed === 0) {
        return fp.apartment_type;
    }

    let display = `${fp.bed} Bedroom`;
    if (fp.bed > 1) {
        display += "s";
    }

    if (fp.is_townhome) {
        display += " Townhome";
    }

    return display;
}

export {FloorPlanCollection};
